/**
 * Utility functions for creating tasks in various development tools
 */

/**
 * Generate a task creation URL for the specified development tool
 */
export function getTaskCreationUrl(
  devTool: 'jira' | 'monday' | 'asana' | 'productboard',
  instanceUrl: string,
  title: string,
  description?: string,
  dataPoints?: number
) {
  const additionalContent = `Our comprehensive analysis has revealed several key insights that make this opportunity particularly compelling:

First, we've identified a clear pattern in user behavior that indicates a strong demand for this functionality. Users consistently attempt to accomplish these tasks through workarounds, suggesting a natural product evolution.

Second, our market research shows that implementing this feature would not only address current user needs but also position us advantageously against competitors. Similar implementations in other platforms have shown significant improvements in user engagement and satisfaction metrics.

Furthermore, the technical foundation required for this implementation aligns well with our current architecture, making this an opportune time to proceed with development. The proposed solution is designed to be scalable and maintainable, with clear upgrade paths for future enhancements.`;

  const keyElements = [
    "Implement AI-powered recommendation engine",
    "Develop user behavior tracking system",
    "Create personalized content delivery system",
    "Build A/B testing framework",
    "Design analytics dashboard"
  ].map(element => `- ${element}`).join('\n');

  const formattedDescription = `${description || ''}\n\n${additionalContent}\n\nKey Elements:\n${keyElements}`;
  
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(formattedDescription);
  
  switch (devTool) {
    case 'jira':
      return `https://${instanceUrl}/secure/CreateIssueDetails!init.jspa?summary=${encodedTitle}&description=${encodedDescription}&pid=10000&issuetype=10002`;
    
    case 'monday':
      return `https://${instanceUrl}/boards/new-item?title=${encodedTitle}&description=${encodedDescription}`;
    
    case 'asana':
      return `https://app.asana.com/0/${instanceUrl}/list?name=${encodedTitle}&notes=${encodedDescription}`;
    
    case 'productboard':
      // For ProductBoard, we don't return a URL as we'll be using the API
      // instead of a direct URL. The instanceUrl here will be the API key.
      return '';
    
    default:
      return '';
  }
}

/**
 * Create a ProductBoard feature using their API
 */
export async function createProductBoardFeature(
  apiKey: string,
  title: string,
  description?: string
) {
  try {
    const formattedDescription = description || '';
    
    const response = await fetch('https://api.productboard.com/features', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Version': '1',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        name: title,
        description: formattedDescription,
        status: 'started' // default status for new features
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create ProductBoard feature: ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating ProductBoard feature:', error);
    throw error;
  }
}

/**
 * Get the instance URL for the specified development tool from localStorage
 */
export function getStoredInstanceUrl(devTool: 'jira' | 'monday' | 'asana' | 'productboard'): string | null {
  const storageKey = `${devTool}InstanceUrl`;
  return localStorage.getItem(storageKey);
}

/**
 * Save the instance URL for the specified development tool to localStorage
 */
export function saveInstanceUrl(devTool: 'jira' | 'monday' | 'asana' | 'productboard', url: string): void {
  const storageKey = `${devTool}InstanceUrl`;
  localStorage.setItem(storageKey, url.trim());
}

/**
 * Get the icon for the specified development tool
 */
export function getDevToolIcon(devTool: 'jira' | 'monday' | 'asana' | 'productboard'): string {
  switch (devTool) {
    case 'asana':
      return "/lovable-uploads/589e044b-84ee-46bb-97ad-505af3f08495.png";
    case 'monday':
      return "/lovable-uploads/6928177a-c9c3-4df1-9d06-f64a4f5edac5.png";
    case 'productboard':
      return "/lovable-uploads/c8ad4133-99c4-45bd-bc13-4da90dc6e99f.png";
    default:
      return "/lovable-uploads/7bb3a14d-1cbc-45ca-9e3c-ba2d642ff27c.png";
  }
}

/**
 * Get the placeholder text for the development tool's instance URL input
 */
export function getInstanceUrlPlaceholder(devTool: 'jira' | 'monday' | 'asana' | 'productboard'): string {
  switch (devTool) {
    case 'jira':
      return 'yourcompany.atlassian.net';
    case 'monday':
      return 'yourcompany.monday.com';
    case 'asana':
      return '123456789';
    case 'productboard':
      return 'your-productboard-api-key';
    default:
      return '';
  }
}

/**
 * Get the label for the development tool's instance URL input
 */
export function getInstanceUrlLabel(devTool: 'jira' | 'monday' | 'asana' | 'productboard'): string {
  switch (devTool) {
    case 'jira':
      return 'Subdomain';
    case 'monday':
      return 'Instance ID';
    case 'asana':
      return 'Workspace ID';
    case 'productboard':
      return 'API Key';
    default:
      return '';
  }
}

/**
 * Get the help text for the development tool's instance URL input
 */
export function getInstanceUrlHelpText(devTool: 'jira' | 'monday' | 'asana' | 'productboard'): string {
  switch (devTool) {
    case 'jira':
      return 'Enter your Jira subdomain (e.g., "yourcompany.atlassian.net")';
    case 'monday':
      return 'Enter your Monday.com instance (e.g., "yourcompany.monday.com")';
    case 'asana':
      return 'Enter your Asana workspace ID';
    case 'productboard':
      return 'Enter your ProductBoard API key for direct integration';
    default:
      return '';
  }
}
